import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PlatDuJourAdmin from './PlatDuJourAdmin';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function MenuManagement() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('MENU', 'edit');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [formules, setFormules] = useState([]);
  const [activeTab, setActiveTab] = useState('items');
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogType, setDialogType] = useState('item');
  const [form, setForm] = useState({});
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const load = async () => {
    try {
      const [c, i, f] = await Promise.all([adminApi.getCategories(), adminApi.getMenuItems(), adminApi.getFormules()]);
      setCategories(c.data);
      setItems(i.data);
      setFormules(f.data);
    } catch { toast.error('Erreur de chargement'); }
  };

  useEffect(() => { load(); }, []);

  const openNew = (type) => {
    setDialogType(type);
    setEditingItem(null);
    if (type === 'category') setForm({ name: '', description: '', order: 0 });
    else if (type === 'formule') setForm({ name: '', description: '', price: '', items: [''] });
    else setForm({ name: '', description: '', price: '', category_id: categories[0]?.id || '', tags: [], active: true, variants: [], supplements: [] });
    setShowDialog(true);
  };

  const openEdit = (item, type) => {
    setDialogType(type);
    setEditingItem(item);
    if (type === 'formule') setForm({ ...item, items: item.items?.length > 0 ? item.items : [''] });
    else setForm({ ...item });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'category') {
        if (editingItem) await adminApi.updateCategory(editingItem.id, form);
        else await adminApi.createCategory(form);
      } else if (dialogType === 'formule') {
        const data = { ...form, price: parseFloat(form.price), items: form.items.filter(i => i.trim()) };
        if (editingItem) await adminApi.updateFormule(editingItem.id, data);
        else await adminApi.createFormule(data);
      } else {
        const data = { ...form, price: form.price ? parseFloat(form.price) : null };
        if (editingItem) await adminApi.updateMenuItem(editingItem.id, data);
        else await adminApi.createMenuItem(data);
      }
      setShowDialog(false);
      load();
      toast.success('Sauvegardé');
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    try {
      if (type === 'category') await adminApi.deleteCategory(id);
      else if (type === 'formule') await adminApi.deleteFormule(id);
      else await adminApi.deleteMenuItem(id);
      load();
      toast.success('Supprimé');
    } catch { toast.error('Erreur'); }
  };

  const toggleItem = async (id) => {
    try {
      await adminApi.toggleMenuItem(id);
      load();
    } catch { toast.error('Erreur'); }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="ml-1 text-charcoal/30" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="ml-1 text-garden-600" /> : <ArrowDown size={12} className="ml-1 text-garden-600" />;
  };

  const sortedItems = [...items].sort((a, b) => {
    let va, vb;
    if (sortField === 'name') { va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); }
    else if (sortField === 'category') {
      va = (categories.find(c => c.id === a.category_id)?.name || '').toLowerCase();
      vb = (categories.find(c => c.id === b.category_id)?.name || '').toLowerCase();
    }
    else if (sortField === 'price') { va = a.price || 0; vb = b.price || 0; }
    else if (sortField === 'active') { va = a.active ? 1 : 0; vb = b.active ? 1 : 0; }
    else { va = a.name; vb = b.name; }
    
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <AdminLayout title="Gestion du Menu">
      <div data-testid="admin-menu-management">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-garden-100 mb-6">
            <TabsTrigger value="items" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">Plats</TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">Catégories</TabsTrigger>
            <TabsTrigger value="formules" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">Formules</TabsTrigger>
              <TabsTrigger value="menu-du-jour" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">Menu du Jour</TabsTrigger>
          </TabsList>

          {/* ITEMS TAB */}
          <TabsContent value="items">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-charcoal/60">{items.length} plats</span>
              {canEdit && (
              <Button onClick={() => openNew('item')} data-testid="admin-add-item-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
                <Plus size={16} className="mr-1" /> Ajouter un plat
              </Button>
              )}
            </div>
            <div className="bg-white rounded-xl border border-garden-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-garden-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-garden-800 cursor-pointer select-none hover:bg-garden-100 transition-colors" onClick={() => handleSort('name')} data-testid="sort-name">
                      <span className="flex items-center">Nom <SortIcon field="name" /></span>
                    </th>
                    <th className="text-left p-3 font-medium text-garden-800 hidden sm:table-cell cursor-pointer select-none hover:bg-garden-100 transition-colors" onClick={() => handleSort('category')} data-testid="sort-category">
                      <span className="flex items-center">Catégorie <SortIcon field="category" /></span>
                    </th>
                    <th className="text-left p-3 font-medium text-garden-800 cursor-pointer select-none hover:bg-garden-100 transition-colors" onClick={() => handleSort('price')} data-testid="sort-price">
                      <span className="flex items-center">Prix <SortIcon field="price" /></span>
                    </th>
                    <th className="text-left p-3 font-medium text-garden-800 hidden md:table-cell cursor-pointer select-none hover:bg-garden-100 transition-colors" onClick={() => handleSort('active')} data-testid="sort-active">
                      <span className="flex items-center">Actif <SortIcon field="active" /></span>
                    </th>
                    <th className="text-right p-3 font-medium text-garden-800">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr key={item.id} className="border-t border-garden-100" data-testid={`admin-item-row-${item.id}`}>
                      <td className="p-3">
                        <span className="font-medium text-charcoal">{item.name}</span>
                        {item.tags?.map(t => <Badge key={t} className="ml-2 text-xs bg-garden-100 text-garden-800 border-0">{t}</Badge>)}
                      </td>
                      <td className="p-3 text-charcoal/70 hidden sm:table-cell">{categories.find(c => c.id === item.category_id)?.name}</td>
                      <td className="p-3 text-charcoal font-medium">{item.price ? `${item.price}€` : '-'}</td>
                      <td className="p-3 hidden md:table-cell">
                        <button onClick={() => toggleItem(item.id)} className={item.active ? 'text-green-600' : 'text-red-400'}>
                          {item.active ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                      </td>
                      <td className="p-3 text-right">
                        {canEdit && (
                        <>
                        <button onClick={() => openEdit(item, 'item')} className="text-garden-600 hover:text-garden-800 mr-2"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(item.id, 'item')} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CATEGORIES TAB */}
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-charcoal/60">{categories.length} catégories</span>
              {canEdit && (
              <Button onClick={() => openNew('category')} data-testid="admin-add-category-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
                <Plus size={16} className="mr-1" /> Ajouter
              </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <div key={cat.id} className="bg-white rounded-xl p-5 border border-garden-200" data-testid={`admin-category-${cat.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-garden-800">{cat.name}</h3>
                      <p className="text-xs text-charcoal/50 mt-1">{cat.description}</p>
                    </div>
                    <div className="flex gap-1">
                      {canEdit && (
                      <>
                      <button onClick={() => openEdit(cat, 'category')} className="text-garden-600 hover:text-garden-800 p-1"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(cat.id, 'category')} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                      </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* FORMULES TAB */}
          <TabsContent value="formules">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-charcoal/60">{formules.length} formules</span>
              {canEdit && (
              <Button onClick={() => openNew('formule')} data-testid="admin-add-formule-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
                <Plus size={16} className="mr-1" /> Ajouter
              </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {formules.map((f) => (
                <div key={f.id} className="bg-white rounded-xl p-5 border border-garden-200" data-testid={`admin-formule-${f.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-garden-800">{f.name}</h3>
                    <span className="font-bold text-garden-800">{f.price}€</span>
                  </div>
                  <ul className="text-xs text-charcoal/70 space-y-1">{f.items?.map((i, idx) => <li key={idx}>+ {i}</li>)}</ul>
                  <div className="flex gap-1 mt-3 justify-end">
                    {canEdit && (
                    <>
                    <button onClick={() => openEdit(f, 'formule')} className="text-garden-600 hover:text-garden-800 p-1"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(f.id, 'formule')} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                    </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
      <TabsContent value="menu-du-jour">
        <PlatDuJourAdmin />
      </TabsContent>
        </Tabs>

        {/* DIALOG */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-garden-800 font-heading">
                {editingItem ? 'Modifier' : 'Ajouter'} {dialogType === 'category' ? 'une catégorie' : dialogType === 'formule' ? 'une formule' : 'un plat'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Nom</Label>
                <Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} className="border-garden-200 rounded-xl" />
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} className="border-garden-200 rounded-xl" rows={2} />
              </div>
              {dialogType !== 'category' && (
                <div>
                  <Label className="text-sm">Prix (€)</Label>
                  <Input type="number" step="0.01" value={form.price || ''} onChange={e => setForm({...form, price: e.target.value})} className="border-garden-200 rounded-xl" />
                </div>
              )}
              {dialogType === 'item' && (
                <>
                  <div>
                    <Label className="text-sm">Catégorie</Label>
                    <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v})}>
                      <SelectTrigger className="border-garden-200 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Tags (séparés par des virgules)</Label>
                    <Input value={(form.tags || []).join(', ')} onChange={e => setForm({...form, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} className="border-garden-200 rounded-xl" placeholder="végétarien, epicé" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={form.active !== false} onCheckedChange={v => setForm({...form, active: v})} />
                    <Label className="text-sm">Actif</Label>
                  </div>
                </>
              )}
              {dialogType === 'formule' && (
                <div>
                  <Label className="text-sm mb-2 block">Éléments inclus</Label>
                  {(form.items || ['']).map((item, i) => (
                    <div key={`formule-item-${i}-${item.slice(0,8)}`} className="flex gap-2 mb-2">
                      <Input value={item} onChange={e => { const newItems = [...(form.items || [''])]; newItems[i] = e.target.value; setForm({...form, items: newItems}); }} className="border-garden-200 rounded-xl" placeholder="Ex: Burger au choix" />
                      {i > 0 && <Button variant="ghost" size="sm" onClick={() => { const newItems = form.items.filter((_, idx) => idx !== i); setForm({...form, items: newItems}); }} className="text-red-400">X</Button>}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => setForm({...form, items: [...(form.items || ['']), '']})} className="text-garden-600">+ Ajouter</Button>
                </div>
              )}
              {dialogType === 'category' && (
                <div>
                  <Label className="text-sm">Ordre d'affichage</Label>
                  <Input type="number" value={form.order || 0} onChange={e => setForm({...form, order: parseInt(e.target.value)})} className="border-garden-200 rounded-xl" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Annuler</Button>
              <Button onClick={handleSave} data-testid="admin-save-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">Sauvegarder</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
